import React, { useState, useEffect, useRef, useCallback } from "react";
import { PiSparkleBold } from "react-icons/pi";
import { PiArrowUpBold } from "react-icons/pi";
import axios from "../../axios";
import apiError from "../../error";
import { SOCKET_EVENTS, socket } from "../../socket";
import "./template-creation-prompt.css";

/**
 * TemplateCreationPrompt
 *
 * Shown inside the right panel when the user clicks "Create Template" but no
 * campaign template has been created yet. The user types a natural-language
 * prompt; on submit the dedicated template-creation agent runs in pra and
 * emits the new campaignTemplateId back over the socket.
 *
 * Props:
 *   attackType            {string}
 *   simulationType        {string}
 *   credentialHarvesting  {boolean}
 *   targetInteractionType {string}
 *   isAIbased             {boolean}
 *   onTemplateCreated     {(campaignTemplateId: number) => void}
 *   onClose               {() => void}
 */
export default function TemplateCreationPrompt({
    attackType,
    simulationType,
    credentialHarvesting,
    targetInteractionType,
    isAIbased,
    onTemplateCreated,
    onClose,
}) {
    const [prompt, setPrompt] = useState("");
    const [status, setStatus] = useState("idle"); // idle | loading | error
    const [errorMsg, setErrorMsg] = useState("");
    const requestIdRef = useRef(null);
    const textareaRef = useRef(null);

    // Auto-resize textarea.
    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
    }, [prompt]);

    const handleSocketEvent = useCallback(
        (data) => {
            if (!data || data.requestId !== requestIdRef.current) return;
            if (data.success && data.campaignTemplateId) {
                setStatus("idle");
                onTemplateCreated(data.campaignTemplateId);
            } else {
                setStatus("error");
                setErrorMsg(data.error || "Template creation failed. Please try again.");
            }
        },
        [onTemplateCreated]
    );

    useEffect(() => {
        if (!socket.connected) socket.connect();
        socket.on(SOCKET_EVENTS.CAMPAIGN_TEMPLATE_CREATED, handleSocketEvent);
        return () => {
            socket.off(SOCKET_EVENTS.CAMPAIGN_TEMPLATE_CREATED, handleSocketEvent);
        };
    }, [handleSocketEvent]);

    const handleSubmit = async () => {
        if (status === "loading") return;

        const trimmed = prompt.trim();
        if (!trimmed) return;

        const requestId = `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        requestIdRef.current = requestId;

        setStatus("loading");
        setErrorMsg("");

        try {
            await axios.post("/api/mcp/create-template", {
                requestId,
                userPrompt: trimmed,
                attackType,
                simulationType,
                credentialHarvesting,
                targetInteractionType,
                isAIbased,
            });
            // Response arrives via socket; keep status as "loading".
        } catch (err) {
            setStatus("error");
            setErrorMsg("Could not reach the server. Please try again.");
            apiError(err);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (status !== "loading") handleSubmit();
        }
    };

    const attackLabel = attackType || "Phishing";
    const simLabel = simulationType || "Simulation Attack";

    return (
        <div className="tcp-wrapper">
            {/* Header */}
            <div className="tcp-topbar">
                <span className="tcp-topbar-title">Create Template</span>
                <button className="tcp-close-btn" onClick={onClose} title="Close" aria-label="Close">
                    x
                </button>
            </div>

            {/* Hero area */}
            <div className="tcp-hero">
                <div className="tcp-icon-ring">
                    <PiSparkleBold className="tcp-sparkle-icon" />
                </div>
                <h2 className="tcp-heading">Enter a prompt to create your campaign template</h2>
                <p className="tcp-sub">
                    Describe the scenario as you want it, and AI will generate the full template - email,
                    landing page, awareness page, and attacker profile.
                </p>

                {/* Context chips */}
                <div className="tcp-chips">
                    <span className="tcp-chip tcp-chip-attack">{attackLabel}</span>
                    <span className="tcp-chip tcp-chip-sim" hidden>{simLabel}</span>
                    {credentialHarvesting && (
                        <span className="tcp-chip tcp-chip-cred" hidden>Credential Harvesting</span>
                    )}
                </div>
            </div>

            {/* Prompt area */}
            <div className="tcp-prompt-section">
                {status === "loading" ? (
                    <div className="tcp-loading-card">
                        <div className="tcp-loading-dots">
                            <span /><span /><span />
                        </div>
                        <p className="tcp-loading-text">
                            AI is building your template - email, landing page
                            {credentialHarvesting ? ", awareness page" : ""}, and attacker profile...
                        </p>
                        <p className="tcp-loading-sub">This usually takes 20-40 seconds.</p>
                    </div>
                ) : (
                    <>
                        <div className="tcp-input-wrap">
                            <textarea
                                ref={textareaRef}
                                className="tcp-textarea"
                                placeholder={`Describe the phishing scenario...\n\nExample: "An urgent IT security alert from Microsoft asking users to verify their account by clicking a link for corporate employees using Outlook."`}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                onKeyDown={handleKeyDown}
                                rows={3}
                                disabled={status === "loading"}
                            />
                            <button
                                className={`tcp-send-btn${!prompt.trim() ? " tcp-send-btn--disabled" : ""}`}
                                onClick={handleSubmit}
                                disabled={!prompt.trim() || status === "loading"}
                                title="Generate template"
                                aria-label="Generate template"
                            >
                                <PiArrowUpBold />
                            </button>
                        </div>
                    </>
                )}

                {status === "error" && (
                    <div className="tcp-error-banner">
                        <span className="tcp-error-icon">!</span>
                        {errorMsg}
                        <button
                            className="tcp-retry-btn"
                            onClick={() => { setStatus("idle"); setErrorMsg(""); }}
                        >
                            Try again
                        </button>
                    </div>
                )}
            </div>

            {/* Tips */}
            {status === "idle" && (
                <div className="tcp-tips">
                    <p className="tcp-tips-label">Tips for a great prompt</p>
                    <ul className="tcp-tips-list">
                        <li>Name the brand being impersonated (e.g. Microsoft, Google, HDFC)</li>
                        <li>Describe the pretext - what urgency or reason does the email claim?</li>
                        <li>Mention the target audience if relevant (e.g. finance team, remote workers)</li>
                    </ul>
                </div>
            )}
        </div>
    );
}
